/* eslint-disable immutable/no-mutation */
/* eslint-disable fp/no-this */
import { User } from '@prismamedia/one-user';
import jwtDecode from 'jwt-decode';
import { localStorage } from './localStorage';

const JWT_LOCALSTORAGE_KEY = 'jwt';
const MAIN_JWT_LOCALSTORAGE_KEY = 'mainJwt';

// eslint-disable-next-line fp/no-class
export class Auth {
  readonly authBaseUrl: string;
  user: User | null = null;

  constructor(authBaseUrl: string) {
    this.authBaseUrl = authBaseUrl;
    if (this.isAuthenticated()) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.user = new User(jwtDecode(this._getJwt()!));
    }
  }

  private _getJwt() {
    return localStorage.get<string>(JWT_LOCALSTORAGE_KEY);
  }

  private _setJwt(jwt: string | null) {
    if (jwt) {
      localStorage.set(JWT_LOCALSTORAGE_KEY, jwt);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.user = new User(jwtDecode(this._getJwt()!));
    } else {
      localStorage.remove(JWT_LOCALSTORAGE_KEY);
      this.user = null;
    }
  }

  private _getMainJwt() {
    return localStorage.get<string>(MAIN_JWT_LOCALSTORAGE_KEY);
  }

  private _setMainJwt(jwt: string | null) {
    jwt
      ? localStorage.set(MAIN_JWT_LOCALSTORAGE_KEY, jwt)
      : localStorage.remove(MAIN_JWT_LOCALSTORAGE_KEY);
  }

  private async _renewJwtIfExpired(jwt?: string): Promise<string | null> {
    if (!jwt) {
      return null;
    }
    const decoded: { exp: number } = jwtDecode(jwt);
    if (decoded.exp * 1000 < Date.now() + 10000) {
      const response = await fetch(`${this.authBaseUrl}/login/renew`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const body = await response.json();
      const newJwt = body?.id_token;
      if (!newJwt) {
        this.logout();
        return null;
      }
      return newJwt;
    }
    return jwt;
  }

  getJwt = async () => {
    const jwt = await this._renewJwtIfExpired(this._getJwt());
    this._setJwt(jwt);
    return jwt;
  };

  getMainJwt = async () => {
    const jwt = await this._renewJwtIfExpired(this._getMainJwt());
    this._setMainJwt(jwt);
    return jwt;
  };

  isAuthenticated = (): this is this & { user: User } => !!this._getJwt();

  isMainAuthenticated = () => !!this._getMainJwt();

  getMainUser = () =>
    this.isMainAuthenticated()
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        new User(jwtDecode(this._getMainJwt()!))
      : null;

  isSuperAdmin = () =>
    !!(this.getMainUser()?.isSuperAdmin() || this.user?.isSuperAdmin());

  login = async (tokenId: string) => {
    const myHeaders = new Headers({
      Authorization: `Bearer ${tokenId}`,
    });

    const myInit: RequestInit = {
      headers: myHeaders,
      mode: 'cors',
      cache: 'default',
    };

    const response = await fetch(`${this.authBaseUrl}/login`, myInit);
    const { id_token } = await response.json();
    this._setJwt(id_token);
    return id_token;
  };

  impersonate = async (userId: string) => {
    const jwt = (await this.getMainJwt()) || (await this.getJwt());
    if (!jwt) {
      return;
    }
    const response = await fetch(
      `${this.authBaseUrl}/impersonification/${userId}`,
      { headers: { Authorization: `Bearer ${jwt}` } },
    );
    const body = await response.json();
    this._setMainJwt(jwt);
    this._setJwt(body.id_token);
    location.reload();
  };

  logout = () => {
    this._setMainJwt(null);
    this._setJwt(null);
    location.reload();
  };

  backToMainUser = () => {
    const mainUser = this._getMainJwt();
    if (mainUser) {
      this._setJwt(mainUser);
      this._setMainJwt(null);
      location.reload();
    }
  };
}
